import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { SERVICES, IMAGES } from "@/siteData";

const PaintingServices = () => (
  <>
    <Seo
      title="Painting Services in Overland Park & KC Metro | JC Colors"
      description="Interior and exterior painting services for homes across Overland Park, Kansas City, Lenexa and Olathe. Request a free estimate from JC Colors."
    />

    <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-12" data-testid="painting-services-page">
      <Reveal>
        <p className="font-mono-accent text-xs uppercase tracking-[0.25em] text-amber-700">
          Painting Services
        </p>
        <h1 className="mt-4 max-w-3xl font-display text-4xl font-extrabold leading-[1.1] tracking-tight text-slate-900 sm:text-5xl">
          Painting contractors for Overland Park, KS and the KC metro
        </h1>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
          JC Colors Remodeling LLC is a residential painting contractor serving
          Overland Park, Kansas City, Lenexa and Olathe. Whether it's one room
          or the whole house — inside or out — every project follows the same
          discipline: protect the space, prep the surface, finish clean.
        </p>
      </Reveal>

      <div className="mt-14 space-y-10">
        {SERVICES.filter((s) => s.slug !== "drywall-repair").map((s, i) => (
          <Reveal key={s.slug} delay={0.05}>
            <Link
              to={`/${s.slug}`}
              data-testid={`painting-services-card-${s.slug}`}
              className={`group grid items-center gap-8 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:shadow-xl md:grid-cols-2 ${
                i % 2 === 1 ? "md:[direction:rtl]" : ""
              }`}
            >
              <div className="overflow-hidden [direction:ltr]">
                <img
                  src={s.image}
                  alt={s.imageAlt}
                  loading="lazy"
                  className="h-72 w-full object-cover transition-transform duration-500 group-hover:scale-105 sm:h-96"
                />
              </div>
              <div className="p-8 [direction:ltr] sm:p-10">
                <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                  {s.title}
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-slate-600 sm:text-base">{s.short}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-amber-700">
                  Learn more about {s.title.toLowerCase()}
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.1} className="mt-10">
        <Link
          to="/drywall-repair"
          data-testid="painting-services-drywall-card"
          className="group grid items-center gap-8 overflow-hidden rounded-3xl bg-slate-900 shadow-sm transition-all duration-300 hover:shadow-xl md:grid-cols-2"
        >
          <div className="p-8 sm:p-12">
            <p className="font-mono-accent text-xs uppercase tracking-[0.25em] text-amber-500">
              The perfect partner to paint
            </p>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
              Drywall repair & finishing
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-slate-300 sm:text-base">
              Great paint over bad drywall is wasted paint. We repair holes,
              cracks and water damage and match your texture before the first
              coat — so the finished wall reads as one smooth surface.
            </p>
            <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-amber-400">
              Explore drywall services
              <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </span>
          </div>
          <div className="overflow-hidden">
            <img
              src={IMAGES.drywall}
              alt="Smooth drywall finish being prepared for paint in a Kansas City home"
              loading="lazy"
              className="h-72 w-full object-cover transition-transform duration-500 group-hover:scale-105 sm:h-full"
            />
          </div>
        </Link>
      </Reveal>

      <Reveal delay={0.1} className="mt-14">
        <div className="flex flex-col items-start justify-between gap-6 rounded-3xl border border-amber-600/30 bg-amber-50 p-8 sm:flex-row sm:items-center sm:p-10">
          <div>
            <h2 className="font-display text-xl font-bold text-slate-900 sm:text-2xl">
              Not sure what your project needs?
            </h2>
            <p className="mt-2 max-w-xl text-sm leading-relaxed text-slate-600">
              Call us or send the estimate form — we'll walk the space, tell you
              honestly what it needs and give you a free estimate.
            </p>
          </div>
          <Link
            to="/contact"
            data-testid="painting-services-estimate-button"
            className="inline-flex items-center gap-2 rounded-full bg-amber-600 px-7 py-3.5 text-sm font-semibold text-white shadow-md transition-all duration-200 hover:scale-105 hover:bg-amber-700"
          >
            Get a Free Estimate
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </Reveal>
    </section>
  </>
);

export default PaintingServices;
